import React, { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Clock, Calendar, CheckCircle, XCircle, Loader2, Trash2, Plus, MapPin, Pencil, Eye, EyeOff, Check } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Dialog Dati Personali
export const PersonalDataDialog = ({ isOpen, onOpenChange, currentUser, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    new_password: '',
    confirm_password: ''
  });

  // Sync form data with currentUser when dialog opens
  React.useEffect(() => {
    if (isOpen && currentUser) {
      setFormData({
        first_name: currentUser.first_name || '',
        last_name: currentUser.last_name || '',
        email: currentUser.email || '',
        phone: currentUser.phone || '',
        new_password: '',
        confirm_password: ''
      });
      setIsEditing(false);
    }
  }, [isOpen, currentUser]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password if provided
    if (formData.new_password) {
      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).{8,}$/;
      if (!passwordRegex.test(formData.new_password)) {
        alert('La password deve contenere almeno 8 caratteri, una maiuscola, una minuscola, un numero e un carattere speciale');
        return;
      }
      if (formData.new_password !== formData.confirm_password) {
        alert('Le password non coincidono');
        return;
      }
    }
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const userId = currentUser.user_id || currentUser.id;

    try {
      const response = await fetch(`${backendUrl}/api/client/profile/${userId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.first_name,
          last_name: formData.last_name,
          phone: formData.phone,
          new_password: formData.new_password || undefined
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      alert('Dati aggiornati con successo!');
      setIsEditing(false);
      setFormData({...formData, new_password: '', confirm_password: ''});
      onUpdate && onUpdate({ ...currentUser, ...formData });
    } catch (error) {
      alert('Errore: ' + error.message);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Dati Personali</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Nome</Label>
            <Input
              value={formData.first_name}
              onChange={(e) => setFormData({...formData, first_name: e.target.value})}
              disabled={!isEditing}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Cognome</Label>
            <Input
              value={formData.last_name}
              onChange={(e) => setFormData({...formData, last_name: e.target.value})}
              disabled={!isEditing}
            />
          </div>
          
          <div className="space-y-2">
            <Label>Email</Label>
            <Input
              type="email"
              value={formData.email}
              disabled
              readOnly
              data-testid="client-email-locked"
            />
          </div>

          <div className="space-y-2">
            <Label>Telefono</Label>
            <Input
              type="tel"
              value={formData.phone}
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              disabled={!isEditing}
              placeholder="+39 123 456 7890"
            />
          </div>

          {isEditing && (
            <>
              <div className="border-t pt-4 mt-4">
                <p className="text-sm text-slate-500 mb-3">Modifica Password (opzionale)</p>
                <div className="space-y-2">
                  <Label>Nuova Password</Label>
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={formData.new_password}
                      onChange={(e) => setFormData({...formData, new_password: e.target.value})}
                      placeholder="Minimo 8 caratteri"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">Maiuscola, minuscola, numero e carattere speciale</p>
                </div>
                
                <div className="space-y-2 mt-3">
                  <Label>Conferma Password</Label>
                  <div className="relative">
                    <Input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirm_password}
                      onChange={(e) => setFormData({...formData, confirm_password: e.target.value})}
                      placeholder="Ripeti la password"
                      className="pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
              </div>
            </>
          )}

          <DialogFooter className="flex gap-2">
            {!isEditing ? (
              <Button type="button" onClick={() => setIsEditing(true)}>
                Modifica
              </Button>
            ) : (
              <>
                <Button type="button" variant="outline" onClick={() => {
                  setIsEditing(false);
                  setFormData({
                    first_name: currentUser?.first_name || '',
                    last_name: currentUser?.last_name || '',
                    email: currentUser?.email || '',
                    phone: currentUser?.phone || '',
                    new_password: '',
                    confirm_password: ''
                  });
                }}>
                  Annulla
                </Button>
                <Button type="submit">Conferma Modifica</Button>
              </>
            )}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Dialog Noleggi
export const RentalsDialog = ({ isOpen, onOpenChange, currentUser }) => {
  const [bookings, setBookings] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [, setTick] = useState(0); // For timer updates

  // Get user_id safely
  const userId = currentUser?.user_id || currentUser?.id;

  const loadBookings = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/user/${userId}`);
      if (response.ok) {
        const data = await response.json();
        setBookings(data || []);
      }
    } catch (error) {
      console.error('Errore caricamento prenotazioni:', error);
    }
    setLoading(false);
  }, [userId]);

  const cancelBooking = async (bookingId) => {
    if (!userId) return;
    if (!window.confirm('Sei sicuro di voler cancellare questa prenotazione?')) {
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/bookings/${bookingId}/cancel`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_id: userId
        })
      });

      if (response.ok) {
        alert('Prenotazione cancellata con successo');
        loadBookings(); // Reload bookings to reflect the change
      } else {
        const error = await response.json();
        alert('Errore nella cancellazione: ' + (error.detail || 'Errore sconosciuto'));
      }
    } catch (error) {
      console.error('Errore cancellazione prenotazione:', error);
      alert('Errore nella cancellazione della prenotazione');
    }
  };

  const loadNotifications = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/${userId}`);
      if (response.ok) {
        const data = await response.json();
        // Filter only booking-related notifications
        const bookingNotifications = (data.notifications || []).filter(n => 
          n.type === 'booking_confirmed' || n.type === 'booking_cancelled'
        );
        setNotifications(bookingNotifications);
      }
    } catch (error) {
      console.error('Errore caricamento notifiche:', error);
    }
  }, [userId]);

  useEffect(() => {
    if (isOpen && userId) {
      loadBookings();
      loadNotifications();
    }
  }, [isOpen, userId, loadBookings, loadNotifications]);

  // Timer update every second
  useEffect(() => {
    if (!isOpen) return;
    
    const interval = setInterval(() => {
      setTick(t => t + 1);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [isOpen]);

  const markNotificationRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      loadNotifications();
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const getBookingStatus = (booking) => {
    const createdAt = new Date(booking.created_at);
    const now = new Date();
    const fourHoursLater = new Date(createdAt.getTime() + 4 * 60 * 60 * 1000);
    const timeRemaining = fourHoursLater - now;
    
    // Check if cancelled
    if (booking.status === 'cancelled') {
      return { status: 'cancelled', label: 'Rifiutata', color: 'text-red-600', bgColor: 'bg-red-100', calendarColor: 'bg-red-200 text-red-800' };
    }
    
    // Check if already confirmed (manually by business owner)
    if (booking.status === 'confirmed' || booking.confirmed_at) {
      return { status: 'confirmed', label: 'Confermata', color: 'text-green-600', bgColor: 'bg-green-100', calendarColor: 'bg-green-200 text-green-800' };
    }
    
    // Still within 4 hours and not confirmed yet - waiting for confirmation
    if (timeRemaining > 0) {
      const hours = Math.floor(timeRemaining / (1000 * 60 * 60));
      const minutes = Math.floor((timeRemaining % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((timeRemaining % (1000 * 60)) / 1000);
      return { 
        status: 'pending', 
        label: 'In attesa', 
        color: 'text-amber-600', 
        bgColor: 'bg-amber-100',
        calendarColor: 'bg-amber-200 text-amber-800',
        timeRemaining: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      };
    }
    
    // Time expired without action - booking auto-confirmed
    return { status: 'confirmed', label: 'Confermata', color: 'text-green-600', bgColor: 'bg-green-100', calendarColor: 'bg-green-200 text-green-800' };
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay: firstDay === 0 ? 6 : firstDay - 1 };
  };

  // Helper function to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Get bookings for a specific day (exclude cancelled for calendar display)
  const getBookingsForDay = (day, includeCancelled = false) => {
    // Create date string for the calendar day (local timezone)
    const calendarDate = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day);
    const dateStr = formatDateLocal(calendarDate);
    
    return bookings.filter(b => {
      // Exclude cancelled bookings from calendar coloring (unless explicitly included)
      if (!includeCancelled && b.status === 'cancelled') {
        return false;
      }
      // Parse booking dates and format in local timezone
      const startDate = new Date(b.start_date);
      const endDate = new Date(b.end_date);
      const start = formatDateLocal(startDate);
      const end = formatDateLocal(endDate);
      return dateStr >= start && dateStr <= end;
    });
  };

  const getDayColor = (day) => {
    // Only show colors for active/pending bookings, NOT cancelled ones
    const dayBookings = getBookingsForDay(day, false); // false = exclude cancelled
    if (dayBookings.length === 0) return '';
    
    // Get the most important status (pending > confirmed)
    const statuses = dayBookings.map(b => getBookingStatus(b).status);
    if (statuses.includes('pending')) return 'bg-amber-200 text-amber-800';
    if (statuses.includes('confirmed')) return 'bg-green-200 text-green-800';
    return '';
  };

  // Sort bookings by start date (chronologically)
  const sortedBookings = [...bookings].sort((a, b) => new Date(a.start_date) - new Date(b.start_date));
  
  // Filter upcoming bookings (not cancelled and start date >= today)
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingBookings = sortedBookings.filter(b => {
    const startDate = new Date(b.start_date);
    return startDate >= today && b.status !== 'cancelled';
  });

  const { days, firstDay } = getDaysInMonth(selectedMonth);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>I Miei Noleggi</DialogTitle>
        </DialogHeader>
        
        {/* Notifications Section */}
        {notifications.filter(n => !n.read).length > 0 && (
          <div className="mb-4 space-y-2">
            <p className="text-sm font-medium text-slate-700">Nuove notifiche:</p>
            {notifications.filter(n => !n.read).map((notification) => (
              <div 
                key={notification.id} 
                className={`p-3 rounded-lg border flex justify-between items-start ${
                  notification.type === 'booking_confirmed' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-start gap-2">
                  {notification.type === 'booking_confirmed' ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className={`text-sm font-medium ${
                      notification.type === 'booking_confirmed' ? 'text-green-800' : 'text-red-800'
                    }`}>
                      {notification.title}
                    </p>
                    <p className={`text-xs ${
                      notification.type === 'booking_confirmed' ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {notification.message}
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => markNotificationRead(notification.id)}
                  className="text-xs text-slate-400 hover:text-slate-600"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
        
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500" />
            <p className="text-slate-500 mt-2">Caricamento...</p>
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">Non hai ancora effettuato noleggi.</p>
            <p className="text-sm text-slate-400 mt-2">I tuoi noleggi appariranno qui quando completerai una prenotazione.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Calendario */}
            <div>
              <div className="flex items-center justify-between mb-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
                >
                  ←
                </Button>
                <span className="font-medium">
                  {selectedMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
                >
                  →
                </Button>
              </div>
              
              {/* Legenda */}
              <div className="flex gap-3 mb-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-green-200 rounded"></div>
                  <span>Confermato</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-amber-200 rounded"></div>
                  <span>In attesa</span>
                </div>
              </div>
              
              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                  <div key={d} className="font-medium text-slate-500 py-2">{d}</div>
                ))}
                
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                
                {Array.from({ length: days }).map((_, i) => {
                  const day = i + 1;
                  const dayColor = getDayColor(day);
                  const dayBookings = getBookingsForDay(day, false); // Exclude cancelled
                  
                  return (
                    <div
                      key={day}
                      className={`p-2 rounded-lg text-center transition-colors ${
                        dayColor || 'hover:bg-slate-100'
                      }`}
                      title={dayBookings.length > 0 ? dayBookings.map(b => b.platform_title).join(', ') : ''}
                    >
                      {day}
                      {dayBookings.length > 0 && <div className="w-1.5 h-1.5 bg-current rounded-full mx-auto mt-1 opacity-60" />}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Riepilogo Noleggi */}
            <div>
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Prossimi Noleggi ({upcomingBookings.length})
              </h3>
              
              {upcomingBookings.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-sm">
                  Nessun noleggio in programma
                </div>
              ) : (
                <div className="space-y-3 max-h-[350px] overflow-y-auto">
                  {upcomingBookings.map((booking) => {
                    const statusInfo = getBookingStatus(booking);
                    const startDate = new Date(booking.start_date);
                    const endDate = new Date(booking.end_date);
                    
                    return (
                      <div key={booking.id} className={`p-3 rounded-lg border ${statusInfo.bgColor}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 text-sm font-medium text-slate-700">
                              <span>
                                {startDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                                {' - '}
                                {endDate.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })}
                              </span>
                            </div>
                            <p className="font-medium text-slate-900 mt-1">{booking.platform_title || 'Piattaforma'}</p>
                            <p className="text-xs text-slate-600 mt-1">
                              📍 {booking.platform_city || 'N/A'}{booking.platform_address ? `, ${booking.platform_address}` : ''}
                            </p>
                          </div>
                          
                          <div className="text-right">
                            <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                              {statusInfo.status === 'confirmed' && <CheckCircle className="h-3 w-3 mr-1" />}
                              {statusInfo.status === 'cancelled' && <XCircle className="h-3 w-3 mr-1" />}
                              {statusInfo.status === 'pending' && <Clock className="h-3 w-3 mr-1" />}
                              {statusInfo.label}
                            </div>
                            
                            {statusInfo.status === 'pending' && statusInfo.timeRemaining && (
                              <div className="mt-1">
                                <p className="text-xs font-mono font-bold text-amber-600">
                                  ⏱ {statusInfo.timeRemaining}
                                </p>
                              </div>
                            )}
                            
                            {/* Cancel button - only show for pending or confirmed bookings that haven't started */}
                            {(statusInfo.status === 'pending' || statusInfo.status === 'confirmed') && startDate > new Date() && (
                              <div className="mt-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => cancelBooking(booking.id)}
                                  className="text-xs px-2 py-1 h-6 text-red-600 border-red-200 hover:bg-red-50"
                                >
                                  Cancella
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {/* Storico */}
              {sortedBookings.filter(b => new Date(b.start_date) < today || b.status === 'cancelled').length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium text-slate-500 mb-2">Storico</h4>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {sortedBookings
                      .filter(b => new Date(b.start_date) < today || b.status === 'cancelled')
                      .slice(0, 5)
                      .map((booking) => {
                        const statusInfo = getBookingStatus(booking);
                        return (
                          <div key={booking.id} className="flex justify-between items-center text-xs p-2 bg-slate-50 rounded">
                            <span className="text-slate-600">
                              {new Date(booking.start_date).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit' })} - {booking.platform_title}
                            </span>
                            <span className={statusInfo.color}>{statusInfo.label}</span>
                          </div>
                        );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Dialog Preferiti
export const FavoritesDialog = ({ isOpen, onOpenChange, favorites = [], tools = [], onRemoveFavorite, onViewDetails, currentUser, onViewPlatform }) => {
  const favoriteTools = Array.isArray(tools) && Array.isArray(favorites) 
    ? tools.filter(t => favorites.includes(t.id))
    : [];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Preferiti</DialogTitle>
        </DialogHeader>
        
        {favoriteTools.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-slate-500">Nessuna piattaforma nei preferiti.</p>
            <p className="text-sm text-slate-400 mt-2">Clicca sul cuoricino nelle card per aggiungere le tue piattaforme preferite!</p>
          </div>
        ) : (
          <div className="grid gap-4">
            {favoriteTools.map(tool => (
              <div key={tool.id} className="flex gap-4 p-4 border rounded-lg hover:bg-slate-50">
                <img 
                  src={tool.images?.[0] || 'https://via.placeholder.com/150'}
                  alt={tool.title}
                  className="w-24 h-24 object-cover rounded"
                />
                <div className="flex-1">
                  <h3 className="font-semibold">{tool.title}</h3>
                  <p className="text-sm text-slate-600">{tool.city}, {tool.province}</p>
                  <p className="text-sm text-slate-500 mt-1">€{tool.daily_rate}/giorno</p>
                </div>
                <div className="flex flex-col gap-2">
                  <Button size="sm" onClick={() => (onViewDetails || onViewPlatform)?.(tool)}>
                    Dettagli
                  </Button>
                  {onRemoveFavorite && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => onRemoveFavorite(tool.id)}
                    >
                      Rimuovi
                    </Button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Dialog Assistenza
export const SupportDialog = ({ isOpen, onOpenChange, currentUser }) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    
    const formDataToSend = new FormData();
    formDataToSend.append('user_id', currentUser.user_id || currentUser.id);
    formDataToSend.append('name', `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim());
    formDataToSend.append('email', currentUser.email);
    formDataToSend.append('subject', formData.subject);
    formDataToSend.append('message', formData.message);
    formDataToSend.append('user_type', 'client');

    try {
      const response = await fetch(`${backendUrl}/api/support/ticket`, {
        method: 'POST',
        body: formDataToSend
      });

      if (!response.ok) throw new Error('Errore invio richiesta');

      setSubmitted(true);
      setFormData({ subject: '', message: '' });
    } catch (error) {
      alert('Errore: ' + error.message);
    }
  };

  const handleClose = () => {
    setSubmitted(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Assistenza Clienti</DialogTitle>
          <DialogDescription>
            Hai bisogno di aiuto? Compila il form e ti risponderemo al più presto.
          </DialogDescription>
        </DialogHeader>
        
        {submitted ? (
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h3 className="font-medium text-slate-900 mb-2">Richiesta Inviata!</h3>
            <p className="text-slate-600">Ti risponderemo il prima possibile via email e nella tua area Notifiche.</p>
            <Button onClick={handleClose} className="mt-4">
              Chiudi
            </Button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input value={`${currentUser?.first_name || ''} ${currentUser?.last_name || ''}`.trim() || 'N/D'} disabled />
            </div>
            
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={currentUser?.email} disabled />
            </div>
            
            <div className="space-y-2">
              <Label>Oggetto *</Label>
              <Input
                value={formData.subject}
                onChange={(e) => setFormData({...formData, subject: e.target.value})}
                placeholder="Problema con prenotazione..."
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label>Messaggio *</Label>
              <Textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                placeholder="Descrivi il problema..."
                rows={5}
                required
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleClose}>
                Annulla
              </Button>
              <Button type="submit">Invia Richiesta</Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Dialog Elimina Account
export const DeleteAccountDialog = ({ isOpen, onOpenChange, currentUser, onLogout }) => {
  const [step, setStep] = useState(1);
  const [reasons, setReasons] = useState([]);
  const [otherReason, setOtherReason] = useState('');

  const toggleReason = (reason) => {
    setReasons(prev => 
      prev.includes(reason) 
        ? prev.filter(r => r !== reason)
        : [...prev, reason]
    );
  };

  const handleDelete = async () => {
    if (reasons.length === 0) {
      alert('Seleziona almeno un motivo');
      return;
    }
    const userId = currentUser?.id || currentUser?.user_id;
    if (!userId) {
      alert("Errore: sessione utente non trovata. Effettua di nuovo il login e riprova.");
      return;
    }

    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const formData = new FormData();
    reasons.forEach(r => formData.append('reasons', r));
    if (otherReason) formData.append('other_reason', otherReason);

    try {
      const response = await fetch(`${backendUrl}/api/client/account/${userId}`, {
        method: 'DELETE',
        body: formData
      });

      if (!response.ok) throw new Error('Errore eliminazione account');

      alert('Account eliminato con successo. Ci dispiace vederti andare!');
      onOpenChange(false);
      if (typeof onLogout === 'function') onLogout();
    } catch (error) {
      alert('Errore: ' + error.message);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      onOpenChange(open);
      if (!open) setStep(1);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-red-600">Elimina Account</DialogTitle>
        </DialogHeader>
        
        {step === 1 ? (
          <div className="space-y-4">
            <p className="text-slate-700">
              Desidera eliminare definitivamente il suo account da <strong>quandomiserve.it</strong>?
            </p>
            <p className="text-sm text-slate-500">
              Questa azione è irreversibile. Tutti i tuoi dati verranno eliminati.
            </p>
            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                No, torna indietro
              </Button>
              <Button variant="destructive" onClick={() => setStep(2)}>
                Sì, elimina
              </Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              Quale &egrave; il motivo dell&apos;eliminazione dell&apos;account? 
              <span className="italic text-slate-500"> (Ci aiuta a migliorare)</span>
            </p>
            
            <div className="space-y-2">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={reasons.includes('not_helpful')}
                  onChange={() => toggleReason('not_helpful')}
                  className="rounded"
                />
                <span className="text-sm">quandomiserve.it non &egrave; stata di mio aiuto per la ricerca della piattaforma aerea</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={reasons.includes('not_expected')}
                  onChange={() => toggleReason('not_expected')}
                  className="rounded"
                />
                <span className="text-sm">quandomiserve.it non era ciò che cercavo</span>
              </label>
              
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={reasons.includes('other')}
                  onChange={() => toggleReason('other')}
                  className="rounded"
                />
                <span className="text-sm">Altro</span>
              </label>
              
              {reasons.includes('other') && (
                <Textarea
                  value={otherReason}
                  onChange={(e) => setOtherReason(e.target.value)}
                  placeholder="Specifica il motivo..."
                  rows={3}
                  className="mt-2"
                />
              )}
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="outline" onClick={() => setStep(1)}>
                Indietro
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Conferma Eliminazione
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Dialog I Miei Noleggi - Agenda personale del cliente
export const MyRentalsDialog = ({ isOpen, onOpenChange, currentUser }) => {
  const [rentals, setRentals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingRental, setEditingRental] = useState(null); // ID del noleggio in modifica
  const [newRental, setNewRental] = useState({
    platform_name: '',
    location: '',
    start_date: '',
    end_date: '',
    notes: '',
    price: ''
  });

  const loadRentals = useCallback(async () => {
    if (!currentUser?.user_id) return;
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/client/my-rentals/${currentUser.user_id}`);
      const data = await response.json();
      setRentals(data.rentals || []);
    } catch (error) {
      console.error('Errore caricamento noleggi:', error);
    }
    setLoading(false);
  }, [currentUser?.user_id]);

  useEffect(() => {
    if (isOpen && currentUser) {
      loadRentals();
    }
  }, [isOpen, currentUser, loadRentals]);

  const addRental = async () => {
    if (!newRental.platform_name || !newRental.start_date || !newRental.end_date) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/client/my-rentals`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: currentUser.user_id,
          ...newRental
        })
      });

      if (response.ok) {
        setNewRental({ platform_name: '', location: '', start_date: '', end_date: '', notes: '', price: '' });
        setShowAddForm(false);
        loadRentals();
      }
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const deleteRental = async (rentalId) => {
    if (!window.confirm('Vuoi eliminare questo noleggio dalla tua agenda?')) return;

    try {
      await fetch(`${BACKEND_URL}/api/client/my-rentals/${rentalId}`, {
        method: 'DELETE'
      });
      loadRentals();
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  // Start editing a rental
  const startEditRental = (rental) => {
    setEditingRental(rental.id);
    setNewRental({
      platform_name: rental.platform_name || '',
      location: rental.location || '',
      start_date: rental.start_date || '',
      end_date: rental.end_date || '',
      notes: rental.notes || '',
      price: rental.price || ''
    });
    setShowAddForm(true);
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingRental(null);
    setNewRental({ platform_name: '', location: '', start_date: '', end_date: '', notes: '', price: '' });
    setShowAddForm(false);
  };

  // Update rental
  const updateRental = async () => {
    if (!newRental.platform_name || !newRental.start_date || !newRental.end_date) {
      alert('Compila tutti i campi obbligatori');
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/client/my-rentals/${editingRental}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newRental)
      });

      if (response.ok) {
        setNewRental({ platform_name: '', location: '', start_date: '', end_date: '', notes: '', price: '' });
        setShowAddForm(false);
        setEditingRental(null);
        loadRentals();
      } else {
        const error = await response.json();
        alert('Errore: ' + (error.detail || 'Errore sconosciuto'));
      }
    } catch (error) {
      console.error('Errore:', error);
      alert('Errore durante l\'aggiornamento');
    }
  };

  // Helper to format date in local timezone
  const formatDateLocal = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return { days, firstDay: firstDay === 0 ? 6 : firstDay - 1 };
  };

  const getRentalForDate = (day) => {
    const dateStr = formatDateLocal(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), day));
    return rentals.find(r => {
      const start = r.start_date;
      const end = r.end_date;
      return dateStr >= start && dateStr <= end;
    });
  };

  const { days, firstDay } = getDaysInMonth(selectedMonth);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-orange-600" />
            I Miei Noleggi
          </DialogTitle>
          <DialogDescription>
            La tua agenda personale per tenere traccia dei noleggi
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-500">Caricamento...</div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Calendar - 3 columns */}
            <div className="lg:col-span-3">
              {/* Month navigation */}
              <div className="flex items-center justify-between mb-4">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() - 1))}
                >
                  ←
                </Button>
                <span className="font-medium capitalize">
                  {selectedMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })}
                </span>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setSelectedMonth(new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1))}
                >
                  →
                </Button>
              </div>

              {/* Legenda */}
              <div className="flex gap-3 mb-3 text-xs">
                <div className="flex items-center gap-1">
                  <div className="w-3 h-3 bg-orange-500 rounded"></div>
                  <span>Noleggio</span>
                </div>
              </div>

              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1 text-center text-sm">
                {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(d => (
                  <div key={d} className="font-medium text-slate-500 py-2">{d}</div>
                ))}
                
                {Array.from({ length: firstDay }).map((_, i) => (
                  <div key={`empty-${i}`} />
                ))}
                
                {Array.from({ length: days }).map((_, i) => {
                  const day = i + 1;
                  const rental = getRentalForDate(day);
                  
                  return (
                    <div
                      key={day}
                      className={`p-2 rounded-lg text-center transition-colors ${
                        rental 
                          ? 'bg-orange-500 text-white font-medium cursor-pointer hover:bg-orange-600' 
                          : 'hover:bg-slate-100'
                      }`}
                      title={rental ? `${rental.platform_name} - ${rental.location}` : ''}
                    >
                      {day}
                    </div>
                  );
                })}
              </div>

              {/* Add button */}
              <div className="mt-4">
                <Button 
                  onClick={() => {
                    setEditingRental(null);
                    setNewRental({ platform_name: '', location: '', start_date: '', end_date: '', notes: '', price: '' });
                    setShowAddForm(!showAddForm);
                  }}
                  className="bg-orange-600 hover:bg-orange-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Aggiungi Noleggio
                </Button>
              </div>

              {/* Add/Edit form */}
              {showAddForm && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg border">
                  <h4 className="font-medium mb-3">
                    {editingRental ? 'Modifica Noleggio' : 'Nuovo Noleggio'}
                  </h4>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm">Nome Piattaforma *</Label>
                      <Input
                        value={newRental.platform_name}
                        onChange={(e) => setNewRental({...newRental, platform_name: e.target.value})}
                        placeholder="es. Autocarrata 20m"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Località *</Label>
                      <Input
                        value={newRental.location}
                        onChange={(e) => setNewRental({...newRental, location: e.target.value})}
                        placeholder="es. Milano, Via Roma 1"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-sm">Data Inizio *</Label>
                        <Input
                          type="date"
                          value={newRental.start_date}
                          onChange={(e) => setNewRental({...newRental, start_date: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label className="text-sm">Data Fine *</Label>
                        <Input
                          type="date"
                          value={newRental.end_date}
                          onChange={(e) => setNewRental({...newRental, end_date: e.target.value})}
                          className="mt-1"
                        />
                      </div>
                    </div>
                    <div>
                      <Label className="text-sm">Prezzo (€)</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newRental.price}
                        onChange={(e) => setNewRental({...newRental, price: e.target.value})}
                        placeholder="es. 150.00"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Note</Label>
                      <Textarea
                        value={newRental.notes}
                        onChange={(e) => setNewRental({...newRental, notes: e.target.value})}
                        placeholder="Note aggiuntive..."
                        rows={2}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={cancelEdit}>
                        Annulla
                      </Button>
                      <Button 
                        onClick={editingRental ? updateRental : addRental} 
                        className={editingRental ? "bg-blue-600 hover:bg-blue-700" : "bg-green-600 hover:bg-green-700"}
                      >
                        {editingRental ? 'Salva Modifiche' : 'Salva'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Rentals List - 2 columns */}
            <div className="lg:col-span-2">
              <h3 className="font-medium mb-3 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                I Tuoi Noleggi ({rentals.length})
              </h3>
              
              {rentals.length === 0 ? (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg">
                  <Calendar className="h-10 w-10 mx-auto mb-2 text-slate-300" />
                  <p className="text-sm">Nessun noleggio registrato</p>
                  <p className="text-xs text-slate-400 mt-1">Clicca "Aggiungi Noleggio" per iniziare</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {rentals
                    .sort((a, b) => a.start_date.localeCompare(b.start_date))
                    .map((rental) => (
                    <div key={rental.id} className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <p className="font-medium text-orange-800 text-sm">
                            {rental.platform_name}
                          </p>
                          <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {rental.location}
                          </p>
                          <p className="text-xs text-slate-600 mt-1">
                            {new Date(rental.start_date).toLocaleDateString('it-IT')}
                            {rental.start_date !== rental.end_date && ` - ${new Date(rental.end_date).toLocaleDateString('it-IT')}`}
                          </p>
                          {rental.price && (
                            <p className="text-xs text-green-700 font-medium mt-1">
                              € {parseFloat(rental.price).toFixed(2)}
                            </p>
                          )}
                          {rental.notes && (
                            <p className="text-xs text-slate-500 mt-1 italic">{rental.notes}</p>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => startEditRental(rental)}
                            className="text-blue-500 hover:text-blue-700 p-1"
                            title="Modifica"
                            data-testid={`edit-rental-${rental.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => deleteRental(rental.id)}
                            className="text-red-500 hover:text-red-700 p-1"
                            title="Elimina"
                            data-testid={`delete-rental-${rental.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// Dialog Notifiche quandomiserve.it
export const NotificationsDialog = ({ isOpen, onOpenChange, currentUser, onNotificationRead }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && currentUser) {
      loadNotifications();
    }
  }, [isOpen, currentUser]);

  const loadNotifications = async () => {
    const userId = currentUser?.id || currentUser?.user_id;
    if (!userId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/notifications/${userId}`);
      if (response.ok) {
        const data = await response.json();
        // Filter only admin/support notifications (not booking-related)
        const adminNotifications = (data.notifications || []).filter(n => 
          n.type === 'support_reply' || 
          n.type === 'admin_notification' || 
          n.type === 'ticket_closed' ||
          n.type === 'platform_approved' ||
          n.type === 'platform_rejected' ||
          n.type === 'platform_suspended' ||
          n.type === 'platform_reactivated' ||
          n.type === 'platform_deleted' ||
          n.type === 'user_suspended' ||
          n.type === 'user_approved' ||
          n.type === 'user_rejected'
        );
        setNotifications(adminNotifications.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
      }
    } catch (error) {
      console.error('Errore caricamento notifiche:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}/read`, {
        method: 'PUT'
      });
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, is_read: true } : n)
      );
      // Notify parent to refresh the counter
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.is_read);
      await Promise.all(
        unreadNotifications.map(n => 
          fetch(`${BACKEND_URL}/api/notifications/${n.id}/read`, { method: 'PUT' })
        )
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      // Notify parent to refresh the counter
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const deleteNotification = async (notificationId) => {
    try {
      await fetch(`${BACKEND_URL}/api/notifications/${notificationId}`, {
        method: 'DELETE'
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      // Notify parent to refresh the counter
      if (onNotificationRead) {
        onNotificationRead();
      }
    } catch (error) {
      console.error('Errore:', error);
    }
  };

  const getNotificationIcon = (type) => {
    switch(type) {
      case 'support_reply':
        return <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center"><CheckCircle className="h-5 w-5 text-blue-600" /></div>;
      case 'ticket_closed':
        return <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center"><XCircle className="h-5 w-5 text-slate-500" /></div>;
      case 'admin_notification':
        return <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center"><Clock className="h-5 w-5 text-orange-600" /></div>;
      default:
        return <div className="w-10 h-10 bg-orange-100 rounded-full flex items-center justify-center"><Clock className="h-5 w-5 text-orange-600" /></div>;
    }
  };

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-orange-600 flex items-center gap-2">
            Notifiche quandomiserve.it
            {unreadCount > 0 && (
              <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">
                {unreadCount} nuov{unreadCount === 1 ? 'a' : 'e'}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Comunicazioni e risposte dal team quandomiserve.it
          </DialogDescription>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Clock className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <p>Nessuna notifica</p>
              <p className="text-sm mt-1">Le risposte alle tue richieste di assistenza appariranno qui</p>
            </div>
          ) : (
            <>
              {unreadCount > 0 && (
                <div className="mb-4 flex justify-end">
                  <button
                    onClick={markAllAsRead}
                    className="text-sm text-orange-600 hover:text-orange-700"
                  >
                    Segna tutte come lette
                  </button>
                </div>
              )}
              <div className="space-y-3">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    className={`p-4 rounded-lg border transition-colors ${
                      notification.is_read 
                        ? 'bg-white border-slate-200' 
                        : 'bg-orange-50 border-orange-200'
                    }`}
                  >
                    <div className="flex gap-3">
                      {getNotificationIcon(notification.type)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <p className={`font-medium ${notification.is_read ? 'text-slate-700' : 'text-slate-800'}`}>
                            {notification.title}
                          </p>
                          {!notification.is_read && (
                            <span className="bg-orange-500 text-white text-xs px-2 py-0.5 rounded-full">Nuovo</span>
                          )}
                        </div>
                        <p className="text-sm text-slate-600 mt-1 whitespace-pre-wrap">
                          {notification.message}
                        </p>
                        {notification.type === 'review_request' && notification.lead_id && (
                          <a
                            href={`/reviews/write/${notification.lead_id}`}
                            className="inline-block mt-2 text-sm bg-orange-600 hover:bg-orange-700 text-white px-3 py-1 rounded-md font-medium"
                            data-testid={`write-review-link-${notification.id}`}
                          >
                            Lascia recensione ★
                          </a>
                        )}
                        <p className="text-xs text-slate-400 mt-2">
                          {new Date(notification.created_at).toLocaleString('it-IT', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {!notification.is_read && (
                          <Button 
                            size="sm" 
                            variant="ghost"
                            onClick={() => markAsRead(notification.id)}
                            title="Segna come letta"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                        )}
                        <Button 
                          size="sm" 
                          variant="ghost"
                          onClick={() => deleteNotification(notification.id)}
                          title="Elimina"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
